<template>
  <DialogComponent
    ref="dialog"
    type="modal"
    :dialogVisible="isOpen"
    @closed="onClosed"
  >
    <template #default>
      <div class="text-center">
        <!-- Success Icon -->
        <div class="flex justify-center mb-4">
          <div
            class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center"
          >
            <svg
              class="w-8 h-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>

        <!-- Title -->
        <h3 class="text-xl font-bold text-gray-800 mb-2">Colis trouvé !</h3>

        <!-- Package Info -->
        <div class="bg-gray-50 rounded-xl p-4 mt-4 text-left">
          <div class="space-y-3">
            <!-- Tracking Code -->
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-500">Numéro de tracking</span>
              <span class="font-semibold text-gray-800">{{
                packageData?.reference ||
                packageData?.trackingCode ||
                packageData?.code ||
                'N/A'
              }}</span>
            </div>

            <!-- Status -->
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-500">Statut</span>
              <span
                class="px-3 py-1 rounded-full text-xs font-semibold"
                :class="getStatusClass(packageData?.status)"
              >
                {{ getStatusLabel(packageData?.status) }}
              </span>
            </div>

            <!-- Origin -->
            <div
              v-if="packageData?.pickup?.address || packageData?.origin"
              class="flex justify-between items-center"
            >
              <span class="text-sm text-gray-500">Origine</span>
              <span class="font-medium text-gray-800 text-right max-w-[60%]">{{
                packageData?.pickup?.address || packageData?.origin
              }}</span>
            </div>

            <!-- Destination -->
            <div
              v-if="
                packageData?.destination?.address || packageData?.destination
              "
              class="flex justify-between items-center"
            >
              <span class="text-sm text-gray-500">Destination</span>
              <span class="font-medium text-gray-800 text-right max-w-[60%]">{{
                packageData?.destination?.address || packageData?.destination
              }}</span>
            </div>

            <!-- Delivery Type -->
            <div
              v-if="packageData?.deliveryType"
              class="flex justify-between items-center"
            >
              <span class="text-sm text-gray-500">Type de livraison</span>
              <span class="font-medium text-gray-800">{{
                getDeliveryTypeLabel(packageData.deliveryType)
              }}</span>
            </div>

            <!-- Pricing -->
            <div
              v-if="packageData?.pricing"
              class="flex justify-between items-center border-t border-gray-200 pt-2 mt-2"
            >
              <span class="text-sm font-semibold text-gray-700"
                >Total à payer</span
              >
              <span class="font-bold text-gray-900"
                >{{ packageData.pricing.total }}
                {{ packageData.pricing.currency }}</span
              >
            </div>
          </div>
        </div>

        <!-- Checkpoints List -->
        <div
          v-if="packageData?.checkpoints && packageData.checkpoints.length > 0"
          class="bg-gray-50 rounded-xl p-4 mt-4 text-left"
        >
          <h4 class="text-md font-bold text-gray-800 mb-3 border-b pb-2">
            Étapes de livraison (Checkpoints)
          </h4>
          <div
            class="space-y-4 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-gray-300 before:to-transparent"
          >
            <div
              v-for="(checkpoint, index) in packageData.checkpoints"
              :key="index"
              class="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active"
            >
              <!-- Timeline indicator -->
              <div
                class="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-indigo-50 text-indigo-500 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2"
              >
                <span class="font-bold text-sm">{{ index + 1 }}</span>
              </div>

              <!-- Content -->
              <div
                class="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-3 rounded shadow"
              >
                <div class="flex flex-col">
                  <span class="font-medium text-gray-800 text-sm mb-1">{{
                    checkpoint.address
                  }}</span>
                  <div
                    class="flex items-center text-xs text-gray-500 gap-1 mt-1"
                  >
                    <svg
                      class="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    <span>{{ checkpoint.contactName }}</span>
                  </div>
                  <div class="flex items-center text-xs text-gray-500 gap-1">
                    <svg
                      class="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                    <span>{{ checkpoint.phone }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="mt-6 space-y-3">
          <!-- View Details Button -->
          <button
            @click="openDetails"
            class="w-full bg-indigo-50 text-indigo-700 py-3 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-indigo-100 transition-colors border border-indigo-100"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Voir les détails
          </button>

          <!-- View Tracking Button -->
          <button
            @click="openTracking"
            class="w-full bg-black text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-gray-800 transition-colors shadow-lg shadow-black/20"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
            Voir le tracking en direct
          </button>

          <div class="flex gap-3">
            <!-- Copy Link Button -->
            <button
              @click="copyTrackingLink"
              class="flex-1 bg-gray-100 text-gray-800 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-gray-200 transition-colors"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              <span v-if="!copied">Copier suivi</span>
              <span v-else class="text-green-600">Copié !</span>
            </button>

            <!-- Copy Payment Link Button (When pending) -->
            <button
              v-if="isPaymentPending"
              @click="copyPaymentLink"
              class="flex-1 bg-orange-100 text-orange-700 py-3 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-orange-200 transition-colors"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              Copier paiement
            </button>
          </div>
        </div>

        <!-- Close Button -->
        <button
          @click="close"
          class="w-full mt-4 text-gray-500 py-2 font-medium hover:text-gray-700 transition-colors"
        >
          Fermer
        </button>
      </div>
    </template>
  </DialogComponent>
</template>

<script setup>
import { ref, computed } from 'vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';
import DialogComponent from '@ui/dialog/DialogComponent.global.vue';
import Commons from '@features/shared/libraries/commons';

const dialog = ref(null);
const packageData = ref(null);
const isOpen = ref(false);
const copied = ref(false);
const emit = defineEmits(['openDetails']);

const trackingCode = computed(
  () =>
    packageData.value?.reference ||
    packageData.value?.trackingCode ||
    packageData.value?.code ||
    ''
);

const isPaymentPending = computed(() => {
  return (
    packageData.value?.paymentStatus !== 'completed' &&
    packageData.value?.paymentMethod !== 'cash'
  );
});

function showResult(data) {
  console.log('🚀 ~ showResult ~ data:', data);
  packageData.value = data.data || data;
  isOpen.value = true;
  copied.value = false;
}

function close() {
  isOpen.value = false;
  copied.value = false;
}

const onClosed = () => {
  isOpen.value = false;
  copied.value = false;
};

function openDetails() {
  if (packageData.value) {
    emit('openDetails', packageData.value);
    close();
  }
}

function openTracking() {
  const code = trackingCode.value;
  if (code) {
    // Open tracking page in new tab
    const baseUrl = constants?.URLS?.packageTrackingBaseUrl || window.location.origin;
    const trackingUrl = `${baseUrl}/track/package/${code}`;
    window.open(trackingUrl, '_blank');
  }
}

async function copyToClipboard(text, successMessage) {
  Commons.copyToClipboard(
    text,
    () => {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        type: 'success',
        title: 'Copié',
        body: successMessage
      });
    },
    () => {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        type: 'error',
        title: 'Erreur',
        body: 'Impossible de copier le texte'
      });
    }
  );
}

async function copyTrackingLink() {
  const code = trackingCode.value;
  if (code) {
    const baseUrl = constants?.URLS?.packageTrackingBaseUrl || window.location.origin;
    const trackingUrl = `${baseUrl}/track/package/${code}`;
    const success = await copyToClipboard(
      trackingUrl,
      'Le lien de suivi a été copié'
    );
    if (success) {
      copied.value = true;
      setTimeout(() => {
        copied.value = false;
      }, 2000);
    }
  }
}

async function copyPaymentLink() {
  const code = trackingCode.value;
  if (code) {
    const baseUrl = constants?.URLS?.packagePaymentBaseUrl || window.location.origin;
    const paymentUrl = `${baseUrl}/pay/package/${code}`;
    await copyToClipboard(paymentUrl, 'Le lien de paiement a été copié');
  }
}

function getStatusClass(status) {
  const statusMap = {
    created: 'bg-gray-100 text-gray-800',
    pending: 'bg-yellow-100 text-yellow-800',
    in_transit: 'bg-blue-100 text-blue-800',
    delivered: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
    picked_up: 'bg-purple-100 text-purple-800',
    out_for_delivery: 'bg-indigo-100 text-indigo-800'
  };
  return statusMap[status] || 'bg-gray-100 text-gray-800';
}

function getStatusLabel(status) {
  const statusLabels = {
    created: 'Créé',
    pending: 'En attente',
    in_transit: 'En transit',
    delivered: 'Livré',
    cancelled: 'Annulé',
    picked_up: 'Collecté',
    out_for_delivery: 'En cours de livraison'
  };
  return statusLabels[status] || status;
}

function getDeliveryTypeLabel(type) {
  const typeLabels = {
    standard: 'Standard',
    express: 'Express',
    priority: 'Prioritaire'
  };
  return typeLabels[type] || type;
}

defineExpose({
  showResult
});
</script>
